import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Pencil, Plus, Save, Trash2, UserRound, Users, ShieldCheck, UserCog } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { AlumniSearch, SearchOption } from "@/components/directories/AlumniSearch";
import { Button } from "@/components/ui/button";

type CommunityMember = {
  id: number;
  name: string;
  email?: string;
};

export type CommunityRecord = {
  id: string;
  name: string;
  description: string;
  leader: CommunityMember | null;
  secretary: CommunityMember | null;
  members: CommunityMember[];
};

const STORAGE_KEY = "iasma-community-management";

const defaultCommunities: CommunityRecord[] = [
  {
    id: "community-1",
    name: "IASMA Pusat",
    description: "Komunitas alumni pusat untuk kegiatan akademik dan sosial.",
    leader: { id: 1, name: "Rina Amelia", email: "rina@example.com" },
    secretary: { id: 2, name: "Dimas Pratama", email: "dimas@example.com" },
    members: [
      { id: 3, name: "Ayu Lestari", email: "ayu@example.com" },
      { id: 4, name: "Budi Santoso", email: "budi@example.com" },
      { id: 5, name: "Citra Putri", email: "citra@example.com" },
    ],
  },
  {
    id: "community-2",
    name: "Pengajian Jumat",
    description: "Komunitas kajian dan pembinaan rohani untuk alumni.",
    leader: { id: 10, name: "Farhan Siregar", email: "farhan@example.com" },
    secretary: { id: 11, name: "Lina Kusuma", email: "lina@example.com" },
    members: [
      { id: 12, name: "Maya Rahma", email: "maya@example.com" },
      { id: 13, name: "Rafli Nanda", email: "rafli@example.com" },
    ],
  },
];

function readCommunityStorage(): CommunityRecord[] {
  if (typeof window === "undefined") return defaultCommunities;

  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (!saved) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultCommunities));
    return defaultCommunities;
  }

  try {
    const parsed = JSON.parse(saved) as CommunityRecord[];
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : defaultCommunities;
  } catch {
    return defaultCommunities;
  }
}

function writeCommunityStorage(items: CommunityRecord[]) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }
}

function toMember(option: SearchOption): CommunityMember {
  return {
    id: option.UserId ?? option.AlumniId,
    name: option.Name,
    email: option.Email,
  };
}

export function CommunityManagementList() {
  const navigate = useNavigate();
  const [communities, setCommunities] = useState<CommunityRecord[]>([]);

  useEffect(() => {
    setCommunities(readCommunityStorage());
  }, []);

  const handleDelete = (communityId: string) => {
    const next = communities.filter((community) => community.id !== communityId);
    setCommunities(next);
    writeCommunityStorage(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Manage Community</h2>
          <p className="text-sm text-slate-500">Kelola nama, deskripsi, dan susunan pengurus komunitas.</p>
        </div>
        <Button type="button" onClick={() => navigate("/community/admin/add")}>
          <Plus className="size-4" />
          Add Community
        </Button>
      </div>

      {communities.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
          Belum ada komunitas. Tambahkan komunitas baru untuk memulai.
        </div>
      ) : (
        <div className="grid gap-4">
          {communities.map((community) => (
            <div key={community.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold text-slate-800">{community.name}</h3>
                  <p className="text-sm text-slate-600">{community.description}</p>
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/community/admin/edit/${community.id}`)}>
                    <Pencil className="size-4" />
                    Edit
                  </Button>
                  <Button type="button" variant="destructive" size="sm" onClick={() => handleDelete(community.id)}>
                    <Trash2 className="size-4" />
                    Delete
                  </Button>
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <UserRound className="size-3.5" /> Leader
                  </div>
                  <div className="text-sm text-slate-800">{community.leader?.name ?? "Belum ditentukan"}</div>
                </div>

                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <ShieldCheck className="size-3.5" /> Secretary
                  </div>
                  <div className="text-sm text-slate-800">{community.secretary?.name ?? "Belum ditentukan"}</div>
                </div>

                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Users className="size-3.5" /> Members
                  </div>
                  <div className="text-sm text-slate-800">{community.members.length} orang</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CommunityManagementForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id);

  const initial = useMemo<CommunityRecord>(() => ({
    id: crypto.randomUUID(),
    name: "",
    description: "",
    leader: null,
    secretary: null,
    members: [],
  }), []);

  const [form, setForm] = useState<CommunityRecord>(initial);

  useEffect(() => {
    if (!editing) {
      setForm(initial);
      return;
    }

    const community = readCommunityStorage().find((item) => item.id === id);
    if (community) {
      setForm(community);
    }
  }, [editing, id, initial]);

  const persist = (next: CommunityRecord) => {
    const list = readCommunityStorage();
    const existing = list.filter((item) => item.id !== next.id);
    const updated = [...existing, next].sort((a, b) => a.name.localeCompare(b.name));
    writeCommunityStorage(updated);
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      window.alert("Nama komunitas wajib diisi.");
      return;
    }

    const next = {
      ...form,
      id: form.id || crypto.randomUUID(),
      name: form.name.trim(),
      description: form.description.trim(),
    };

    persist(next);
    navigate("/community/admin");
  };

  const setLeader = (selected: SearchOption | null) => {
    setForm((prev) => ({ ...prev, leader: selected ? toMember(selected) : null }));
  };

  const setSecretary = (selected: SearchOption | null) => {
    setForm((prev) => ({ ...prev, secretary: selected ? toMember(selected) : null }));
  };

  const addMember = (selected: SearchOption | null) => {
    if (!selected) return;

    const member = toMember(selected);
    setForm((prev) => {
      if (prev.members.some((item) => item.id === member.id)) return prev;
      return { ...prev, members: [...prev.members, member] };
    });
  };

  const removeMember = (memberId: number) => {
    setForm((prev) => ({
      ...prev,
      members: prev.members.filter((member) => member.id !== memberId),
    }));
  };

  const handleDelete = () => {
    if (!editing) return;
    if (!window.confirm("Yakin ingin menghapus komunitas ini?")) return;

    const list = readCommunityStorage().filter((item) => item.id !== id);
    writeCommunityStorage(list);
    navigate("/community/admin");
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{editing ? "Edit Community" : "Add Community"}</h2>
          <p className="text-sm text-slate-500">Isi data komunitas dan pilih alumni yang menjabat sebagai pemimpin, sekretaris, dan anggota.</p>
        </div>
        <Button type="button" variant="outline" onClick={() => navigate("/community/admin")}>
          <ArrowLeft className="size-4" />
          Back
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2 space-y-1">
          <label className="block text-sm font-medium text-slate-700">Nama Komunitas</label>
          <input
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none"
            placeholder="Contoh: Keluarga Alumni 2004"
          />
        </div>

        <div className="md:col-span-2 space-y-1">
          <label className="block text-sm font-medium text-slate-700">Deskripsi</label>
          <textarea
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            rows={4}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none"
            placeholder="Tuliskan tujuan dan kegiatan komunitas..."
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Leader</label>
          <AlumniSearch onSelected={setLeader} userOnly={true} />
          {form.leader && (
            <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <span>{form.leader.name}</span>
              <button type="button" className="text-xs text-slate-500 hover:text-slate-700" onClick={() => setLeader(null)}>Clear</button>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Secretary</label>
          <AlumniSearch onSelected={setSecretary} userOnly={true} />
          {form.secretary && (
            <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <span>{form.secretary.name}</span>
              <button type="button" className="text-xs text-slate-500 hover:text-slate-700" onClick={() => setSecretary(null)}>Clear</button>
            </div>
          )}
        </div>

        <div className="md:col-span-2 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <label className="block text-sm font-medium text-slate-700">Members</label>
            <div className="w-full max-w-md">
              <AlumniSearch onSelected={addMember} userOnly={true} />
            </div>
          </div>

          {form.members.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-500">
              Belum ada anggota yang ditambahkan.
            </div>
          ) : (
            <div className="space-y-2">
              {form.members.map((member) => (
                <div key={member.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                  <span>{member.name}</span>
                  <button type="button" className="text-xs text-red-500 hover:text-red-700" onClick={() => removeMember(member.id)}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
        {editing && (
          <Button type="button" variant="destructive" onClick={handleDelete}>
            <Trash2 className="size-4" />
            Delete
          </Button>
        )}
        <Button type="button" variant="outline" onClick={() => navigate("/community/admin")}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSave}>
          <Save className="size-4" />
          Save
        </Button>
      </div>
    </div>
  );
}
